import React, { useEffect, useState, useCallback, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { fetchUserByEmail, syncUser, fetchCards, fetchCardLinks, User, UserCreate, Card, CardLink } from "../utils/api";
import Button from "./ui/Button";

interface SignatureData {
  name: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  websiteUrl: string;
  cardUrl: string;
  showPhone: boolean;
  showWebsite: boolean;
  showCardLink: boolean;
  showPhoto: boolean;
  photoUrl: string;
  accentColor: string;
  layout: 'horizontal' | 'vertical' | 'minimal';
  linkLabels: { title: string; url: string; show: boolean }[];
}

const ACCENT_COLORS = [
  { name: 'WhoBrew Blue', value: '#2378b6' },
  { name: 'Blue', value: '#2563eb' },
  { name: 'Green', value: '#059669' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Navy', value: '#193f60' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Black', value: '#1f2937' },
  { name: 'Teal', value: '#0d9488' },
];

const EmailSignature: React.FC = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [sig, setSig] = useState<SignatureData>({
    name: '', title: '', company: '', email: '', phone: '', websiteUrl: '', cardUrl: '',
    showPhone: true, showWebsite: true, showCardLink: true, showPhoto: true, photoUrl: '',
    accentColor: '#2378b6', layout: 'horizontal', linkLabels: [],
  });
  const [copied, setCopied] = useState(false);
  const signatureRef = useRef<HTMLDivElement>(null);

  useEffect(() => { document.title = "Email Signature - WhoBrew"; }, []);

  const loadData = useCallback(async () => {
    if (!user) return;
    const email = user.primaryEmailAddress?.emailAddress;
    if (!email) return;
    setLoading(true);
    try {
      let fetchedUser: User;
      try { fetchedUser = await fetchUserByEmail(email); }
      catch { fetchedUser = await syncUser({ username: email.split("@")[0], email } as UserCreate); }

      const cards = await fetchCards(fetchedUser.id).catch(() => [] as Card[]);
      const card = cards[0];

      const links = card ? await fetchCardLinks(card.id).catch(() => [] as CardLink[]) : [];
      const topLinks = links.slice(0, 3).map(l => ({ title: l.title, url: l.url, show: true }));

      setSig({
        name: card?.name || `${fetchedUser.firstName || ''} ${fetchedUser.lastName || ''}`.trim(),
        title: card?.position || '',
        company: card?.company || fetchedUser.company || '',
        email: card?.email || fetchedUser.email,
        phone: card?.phoneNumber || fetchedUser.phoneNumber || '',
        websiteUrl: card?.websiteUrl || '',
        // BL-008: the public card URL is the account handle, not the per-card slug.
        cardUrl: card && fetchedUser.username ? `https://whobrew.us/card/${fetchedUser.username}` : '',
        showPhone: true, showWebsite: !!card?.websiteUrl, showCardLink: true, showPhoto: true,
        photoUrl: card?.profilePhoto || card?.squareLogo || user?.imageUrl || '',
        accentColor: '#2378b6',
        layout: 'horizontal',
        linkLabels: topLinks,
      });
    } catch {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  const generateHtml = (): string => {
    const s = sig;
    const accent = s.accentColor;
    const divider = `<span style="color: ${accent}; margin: 0 6px;">|</span>`;

    const contactParts: string[] = [];
    if (s.email) contactParts.push(`<a href="mailto:${s.email}" style="color: #555; text-decoration: none; font-size: 13px;">${s.email}</a>`);
    if (s.showPhone && s.phone) contactParts.push(`<a href="tel:${s.phone}" style="color: #555; text-decoration: none; font-size: 13px;">${s.phone}</a>`);
    if (s.showWebsite && s.websiteUrl) contactParts.push(`<a href="${s.websiteUrl}" style="color: ${accent}; text-decoration: none; font-size: 13px;">${s.websiteUrl.replace(/^https?:\/\//, '')}</a>`);

    const linkParts = s.linkLabels.filter(l => l.show && l.url).map(l =>
      `<a href="${l.url}" style="color: ${accent}; text-decoration: none; font-size: 12px; font-weight: 500;">${l.title}</a>`
    );

    const cardButton = s.showCardLink && s.cardUrl
      ? `<a href="${s.cardUrl}" style="display: inline-block; background-color: ${accent}; color: white; padding: 6px 16px; border-radius: 4px; text-decoration: none; font-size: 12px; font-weight: 600; margin-top: 8px;">View My Card</a>`
      : '';

    if (s.layout === 'minimal') {
      return `<table cellpadding="0" cellspacing="0" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <tr><td>
          <p style="margin: 0; font-size: 15px; font-weight: 600; color: #1a1a1a;">${s.name}</p>
          ${s.title ? `<p style="margin: 2px 0 0; font-size: 13px; color: #666;">${s.title}${s.company ? ` at ${s.company}` : ''}</p>` : ''}
          <p style="margin: 6px 0 0; font-size: 13px;">${contactParts.join(divider)}</p>
          ${cardButton ? `<p style="margin: 8px 0 0;">${cardButton}</p>` : ''}
        </td></tr>
      </table>`;
    }

    if (s.layout === 'vertical') {
      return `<table cellpadding="0" cellspacing="0" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <tr><td style="padding-bottom: 8px; border-bottom: 2px solid ${accent};">
          ${s.showPhoto && s.photoUrl ? `<img src="${s.photoUrl}" width="60" height="60" style="border-radius: 50%; margin-bottom: 8px; display: block;" />` : ''}
          <p style="margin: 0; font-size: 17px; font-weight: 700; color: #1a1a1a;">${s.name}</p>
          ${s.title ? `<p style="margin: 2px 0 0; font-size: 13px; color: ${accent}; font-weight: 500;">${s.title}</p>` : ''}
          ${s.company ? `<p style="margin: 1px 0 0; font-size: 13px; color: #888;">${s.company}</p>` : ''}
        </td></tr>
        <tr><td style="padding-top: 8px;">
          ${contactParts.map(p => `<p style="margin: 2px 0;">${p}</p>`).join('')}
          ${linkParts.length > 0 ? `<p style="margin: 6px 0 0;">${linkParts.join(' &nbsp; ')}</p>` : ''}
          ${cardButton ? `<p style="margin: 0;">${cardButton}</p>` : ''}
        </td></tr>
      </table>`;
    }

    // Horizontal (default)
    return `<table cellpadding="0" cellspacing="0" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      <tr>
        ${s.showPhoto && s.photoUrl ? `<td style="vertical-align: top; padding-right: 14px;">
          <img src="${s.photoUrl}" width="70" height="70" style="border-radius: 50%;" />
        </td>` : ''}
        <td style="vertical-align: top;${s.showPhoto && s.photoUrl ? ' border-left: 2px solid ' + accent + '; padding-left: 14px;' : ''}">
          <p style="margin: 0; font-size: 17px; font-weight: 700; color: #1a1a1a;">${s.name}</p>
          ${s.title ? `<p style="margin: 2px 0 0; font-size: 13px; color: ${accent}; font-weight: 500;">${s.title}</p>` : ''}
          ${s.company ? `<p style="margin: 1px 0 0; font-size: 13px; color: #888;">${s.company}</p>` : ''}
          <p style="margin: 6px 0 0; font-size: 13px;">${contactParts.join(divider)}</p>
          ${linkParts.length > 0 ? `<p style="margin: 4px 0 0;">${linkParts.join(' &nbsp; ')}</p>` : ''}
          ${cardButton ? `<p style="margin: 0;">${cardButton}</p>` : ''}
        </td>
      </tr>
    </table>`;
  };

  const handleCopy = async () => {
    const html = generateHtml();
    try {
      // Copy as rich HTML so it pastes formatted in email clients
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback: copy raw HTML
      try {
        await navigator.clipboard.writeText(html);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch {}
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
        <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="animate-pulse space-y-2">
              <div className="h-3 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded w-32" />
              <div className="h-8 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl w-64" />
              <div className="h-4 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded w-72" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-pulse">
            <div className="h-96 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl" />
            <div className="space-y-4">
              <div className="h-64 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl" />
              <div className="h-32 bg-coffee-50 dark:bg-coffee-900 border border-coffee-100 dark:border-coffee-800 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
      {/* Page header */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow mb-1.5">Email Signature</div>
              <h1 className="text-display text-3xl sm:text-4xl text-coffee-900 dark:text-cream-50">Your email signature</h1>
              <p className="mt-1.5 text-coffee-600 dark:text-cream-200">Generate a signature for Gmail, Outlook, or Apple Mail.</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 pt-1">
              <Button
                variant={copied ? 'secondary' : 'primary'}
                onClick={handleCopy}
              >
                {copied ? 'Copied!' : 'Copy signature'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content — two columns */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

          {/* Left: form */}
          <div className="space-y-4">
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-6 shadow-coffee space-y-5">
              <div className="eyebrow mb-1">Customise</div>

              {/* Layout picker */}
              <div>
                <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">Layout</label>
                <div className="inline-flex gap-1 bg-white border border-coffee-100 dark:border-coffee-800 rounded-xl p-1">
                  {(['horizontal', 'vertical', 'minimal'] as const).map(l => (
                    <button
                      key={l}
                      onClick={() => setSig({...sig, layout: l})}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg capitalize transition-colors ${
                        sig.layout === l ? 'bg-coffee-900 text-white' : 'text-coffee-600 hover:text-coffee-900'
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block eyebrow mb-1.5 text-coffee-500 dark:text-cream-300">Name</label>
                  <input value={sig.name} onChange={e => setSig({...sig, name: e.target.value})} className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                </div>
                <div>
                  <label className="block eyebrow mb-1.5 text-coffee-500 dark:text-cream-300">Title</label>
                  <input value={sig.title} onChange={e => setSig({...sig, title: e.target.value})} className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                </div>
                <div>
                  <label className="block eyebrow mb-1.5 text-coffee-500 dark:text-cream-300">Company</label>
                  <input value={sig.company} onChange={e => setSig({...sig, company: e.target.value})} className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                </div>
                <div>
                  <label className="block eyebrow mb-1.5 text-coffee-500 dark:text-cream-300">Email</label>
                  <input value={sig.email} onChange={e => setSig({...sig, email: e.target.value})} className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                </div>
                <div>
                  <label className="block eyebrow mb-1.5 text-coffee-500 dark:text-cream-300">Phone</label>
                  <input value={sig.phone} onChange={e => setSig({...sig, phone: e.target.value})} className="w-full px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm bg-white text-coffee-900 dark:text-cream-50 focus:ring-2 focus:ring-coffee-500 focus:border-coffee-500" />
                </div>
              </div>

              {/* Accent color */}
              <div>
                <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">Accent color</label>
                <div className="flex gap-2 flex-wrap">
                  {ACCENT_COLORS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setSig({...sig, accentColor: c.value})}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${sig.accentColor === c.value ? 'border-coffee-900 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c.value }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-0 border-t border-coffee-100 dark:border-coffee-800 pt-4">
                {[
                  { key: 'showPhone', label: 'Show phone number' },
                  { key: 'showWebsite', label: 'Show website' },
                  { key: 'showCardLink', label: 'Show "View My Card" button' },
                  { key: 'showPhoto', label: 'Show profile photo' },
                ].map((toggle, idx, arr) => (
                  <div
                    key={toggle.key}
                    className={`flex items-center justify-between py-3 ${idx < arr.length - 1 ? 'border-b border-coffee-100 dark:border-coffee-800' : ''}`}
                  >
                    <span className="text-sm text-coffee-700 dark:text-cream-200">{toggle.label}</span>
                    <button
                      type="button"
                      onClick={() => setSig({...sig, [toggle.key]: !(sig as any)[toggle.key]})}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${(sig as any)[toggle.key] ? 'bg-coffee-600' : 'bg-coffee-200'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${(sig as any)[toggle.key] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Link toggles */}
              {sig.linkLabels.length > 0 && (
                <div>
                  <label className="block eyebrow mb-2 text-coffee-500 dark:text-cream-300">Include links</label>
                  <div className="space-y-0 border border-coffee-100 dark:border-coffee-800 rounded-xl overflow-hidden">
                    {sig.linkLabels.map((link, i) => (
                      <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${i < sig.linkLabels.length - 1 ? 'border-b border-coffee-100 dark:border-coffee-800' : ''}`}>
                        <span className="text-sm text-coffee-700 dark:text-cream-200">{link.title}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...sig.linkLabels];
                            updated[i] = { ...updated[i], show: !updated[i].show };
                            setSig({ ...sig, linkLabels: updated });
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${link.show ? 'bg-coffee-600' : 'bg-coffee-200'}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${link.show ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: live preview + instructions */}
          <div className="space-y-4 lg:sticky lg:top-4">
            {/* Window-chrome preview card */}
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl shadow-coffee overflow-hidden">
              {/* Chrome bar */}
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-coffee-100 dark:border-coffee-800 bg-coffee-50 dark:bg-coffee-900">
                <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                <span className="w-2.5 h-2.5 rounded-full bg-coffee-200" />
                <span className="ml-3 text-[10px] font-mono text-coffee-400 dark:text-cream-400">New Message — signature preview</span>
              </div>
              <div className="p-6">
                <p className="text-[10px] eyebrow mb-4 text-coffee-400 dark:text-cream-400">— Your signature appears below the message body</p>
                <div ref={signatureRef} dangerouslySetInnerHTML={{ __html: generateHtml() }} />
              </div>
            </div>

            {/* Copy action */}
            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-5 shadow-coffee">
              <div className="eyebrow mb-3">Copy your signature</div>
              <Button
                variant={copied ? 'secondary' : 'primary'}
                fullWidth
                onClick={handleCopy}
              >
                {copied ? 'Copied to clipboard' : 'Copy signature'}
              </Button>
              <p className="text-xs text-coffee-500 dark:text-cream-300 mt-2 text-center">
                Copies formatted HTML — paste directly into your email client.
              </p>
            </div>

            {/* Step instructions */}
            <div className="bg-coffee-900 text-white rounded-2xl p-5 relative overflow-hidden">
              <div className="absolute -top-16 -right-12 w-40 h-40 rounded-full bg-coffee-700 blur-3xl opacity-40" />
              <div className="relative">
                <div className="eyebrow text-coffee-200 mb-3">How to install</div>
                <div className="space-y-3 text-sm text-coffee-200">
                  <div>
                    <p className="font-semibold text-white">Gmail</p>
                    <p>Settings &rarr; See all settings &rarr; General &rarr; Signature &rarr; Paste</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Outlook</p>
                    <p>Settings &rarr; Mail &rarr; Compose and reply &rarr; Email signature &rarr; Paste</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white">Apple Mail</p>
                    <p>Mail &rarr; Settings &rarr; Signatures &rarr; Create new &rarr; Paste</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EmailSignature;
